/**
 * Boolean project settings only org admins may change, rendered as toggles on
 * the "Project settings" sub-tab. Two storage shapes:
 *  - `extra_attribute`: a key under `source_entity_extra_attributes`
 *    (the API enforces the org-admin role for these);
 *  - `column`: a top-level project column, optionally mapped through
 *    `toValue` / `fromValue` when the column is not a plain boolean.
 */
export type ProjectAdminFlagKey =
  | 'dwr_reporting_hierarchy_enabled'
  | 'dwr_reporting_hierarchy_self_approve'
  | 'restrict_report_edits_to_creator'
  | 'sample_collection_enabled'
  | 'issue_workflow_enabled'
  | 'report_map_export_enabled'
  | 'station_range_location_enabled'
  | 'new_labor_format'
  | 'offline_support';

export type ProjectAdminFlagSection = 'Daily reports' | 'Project';

export interface ProjectAdminFlag {
  key: ProjectAdminFlagKey;
  section: ProjectAdminFlagSection;
  title: string;
  description: string;
  enabledMessage: string;
  disabledMessage: string;
  storage: 'extra_attribute' | 'column';
  /** Column name when `storage` is `column`; defaults to `key`. */
  column?: string;
  /** Column value for a given toggle state (column storage only). */
  toValue?: (enabled: boolean) => unknown;
  /** Toggle state for a given column value (column storage only). */
  fromValue?: (value: unknown) => boolean;
  /** Only shown while this other flag is on (it has no effect otherwise). */
  requires?: ProjectAdminFlagKey;
}

export type ProjectAdminFlagState = Record<ProjectAdminFlagKey, boolean>;

/** The slice of a project payload these helpers read and write. */
export interface ProjectAdminFlagHolder {
  source_entity_extra_attributes?: Record<string, unknown> | null;
  cover_photo?: { id?: string } | null;
  cover_photo_id?: string;
  new_labor_format?: boolean;
  offline_support?: boolean;
  daily_report_location_mode?: string | null;
}

export const PROJECT_ADMIN_FLAGS: ProjectAdminFlag[] = [
  {
    key: 'dwr_reporting_hierarchy_enabled',
    section: 'Daily reports',
    storage: 'extra_attribute',
    title: 'Use reporting hierarchy for DWR approvals',
    description: 'Only upstream lead inspectors and PMs can review or approve DWRs.',
    enabledMessage: 'DWR reporting hierarchy enabled',
    disabledMessage: 'DWR reporting hierarchy disabled',
  },
  {
    key: 'dwr_reporting_hierarchy_self_approve',
    section: 'Daily reports',
    storage: 'extra_attribute',
    requires: 'dwr_reporting_hierarchy_enabled',
    title: 'Let PMs approve their own DWRs',
    description:
      'With the reporting hierarchy on, project managers can approve DWRs they created themselves instead of ' +
      'submitting them up the chain. Inspectors are unaffected.',
    enabledMessage: 'PM self-approval of DWRs enabled',
    disabledMessage: 'PM self-approval of DWRs disabled',
  },
  {
    key: 'restrict_report_edits_to_creator',
    section: 'Daily reports',
    storage: 'extra_attribute',
    title: 'Only the report creator can edit daily reports',
    description:
      'Inspectors can view, but not edit, delete, submit or withdraw, daily reports created by other inspectors. ' +
      'Lead inspectors and PMs are unaffected.',
    enabledMessage: 'Daily report edits restricted to the report creator',
    disabledMessage: 'Daily report edit restriction removed',
  },
  {
    key: 'sample_collection_enabled',
    section: 'Daily reports',
    storage: 'extra_attribute',
    title: 'Sample collection',
    description:
      'Show the Samples tab and "+ Create sample" on material acceptance records for this project. ' +
      'Only applies when the organization rolls sample collection out project by project.',
    enabledMessage: 'Sample collection enabled',
    disabledMessage: 'Sample collection disabled',
  },
  {
    key: 'station_range_location_enabled',
    section: 'Daily reports',
    storage: 'column',
    column: 'daily_report_location_mode',
    toValue: enabled => (enabled ? 'station_range' : 'pin'),
    fromValue: value => value === 'station_range',
    title: 'Location mode: station range',
    description: 'Daily report items are located by station range instead of a map pin.',
    enabledMessage: 'Station range location mode enabled',
    disabledMessage: 'Map pin location mode enabled',
  },
  {
    key: 'report_map_export_enabled',
    section: 'Daily reports',
    storage: 'extra_attribute',
    title: 'Map data export',
    description:
      'Show "Export map data" (KMZ / GeoJSON) on the Daily reports tab. The file holds the shapes, locations, ' +
      'comments, inspector names and photo links of the filtered reports, and anyone it is shared with can open the photos.',
    enabledMessage: 'Map data export enabled',
    disabledMessage: 'Map data export disabled',
  },
  {
    key: 'new_labor_format',
    section: 'Project',
    storage: 'column',
    title: 'New labor format',
    description: 'Use the newer labor and equipment entry format on daily reports.',
    enabledMessage: 'New labor format enabled',
    disabledMessage: 'New labor format disabled',
  },
  {
    key: 'offline_support',
    section: 'Project',
    storage: 'column',
    title: 'Offline support',
    description: 'Allow the mobile app to work on this project without a connection.',
    enabledMessage: 'Offline support enabled',
    disabledMessage: 'Offline support disabled',
  },
  {
    key: 'issue_workflow_enabled',
    section: 'Project',
    storage: 'extra_attribute',
    title: 'Issue contractor workflow',
    description:
      'Public issues on this project can be forwarded to contractor users, who respond or forward them to other contractor users. ' +
      'Project managers, project owners, reviewers, or the inspector who created the issue, can close it at any time.',
    enabledMessage: 'Issue contractor workflow enabled',
    disabledMessage: 'Issue contractor workflow disabled',
  },
];

export const PROJECT_ADMIN_FLAG_SECTIONS: ProjectAdminFlagSection[] = [
  ...new Set(PROJECT_ADMIN_FLAGS.map(f => f.section)),
];

/** Whether a flag should be offered given the current toggle state. */
export function isProjectAdminFlagVisible(flag: ProjectAdminFlag, state: ProjectAdminFlagState): boolean {
  return !flag.requires || state[flag.requires] === true;
}

export function readProjectAdminFlags(project: ProjectAdminFlagHolder | null | undefined): ProjectAdminFlagState {
  const attrs = project?.source_entity_extra_attributes || {};
  return PROJECT_ADMIN_FLAGS.reduce((state, flag) => {
    if (flag.storage === 'extra_attribute') {
      state[flag.key] = attrs[flag.key] === true;
    } else {
      const value = (project as Record<string, unknown> | null | undefined)?.[flag.column ?? flag.key];
      state[flag.key] = flag.fromValue ? flag.fromValue(value) : value === true;
    }
    return state;
  }, {} as ProjectAdminFlagState);
}

export function buildProjectAdminFlagUpdate<T extends ProjectAdminFlagHolder>(
  projectPayload: T,
  key: ProjectAdminFlagKey,
  enabled: boolean
): { payload: T; successMessage: string } {
  const flag = PROJECT_ADMIN_FLAGS.find(f => f.key === key);
  const payload: T = { ...projectPayload };
  if (flag.storage === 'extra_attribute') {
    payload.source_entity_extra_attributes = {
      ...(projectPayload?.source_entity_extra_attributes || {}),
      [key]: enabled,
    };
  } else {
    (payload as Record<string, unknown>)[flag.column ?? flag.key] = flag.toValue ? flag.toValue(enabled) : enabled;
  }
  // Turning a flag off also resets anything that depends on it, so a dependent
  // toggle comes back off the next time the parent is enabled.
  if (!enabled) {
    for (const dependent of PROJECT_ADMIN_FLAGS.filter(f => f.requires === key && f.storage === 'extra_attribute')) {
      payload.source_entity_extra_attributes = {
        ...(payload.source_entity_extra_attributes || {}),
        [dependent.key]: false,
      };
    }
  }
  // The store payload only carries the nested cover_photo (the API strips FK ids).
  // Send the id back explicitly so the project save keeps the cover photo attached.
  const coverPhotoId = projectPayload?.cover_photo?.id;
  if (coverPhotoId) {
    payload.cover_photo_id = coverPhotoId;
  }
  return {
    payload,
    successMessage: enabled ? flag.enabledMessage : flag.disabledMessage,
  };
}
